/**
 * FollowUpsPanel (13.2-07; D-13.2-A-4, D-13.2-P-7): what happens after lodging, as dated follow-ups on the incident.
 *
 * - **The list** (`useFollowUps`): each follow-up's title, owner role (and "you" when it is yours), due day
 *   `D MMM YYYY` and a state chip: Done, Scheduled, Overdue (amber), No due date. A worker-update follow-up is marked.
 * - **Add a follow-up** (CareManager, HRWHS or Oversight on an open incident, `canAddFollowUp`): what, details, the
 *   owner role (default the person's own), the due date (required, today or later), and "This is the injured worker's
 *   update" (kind `worker-update`), shown only when `WorkerInjured = Yes`. The owner is always the person adding it:
 *   there is no people picker in the dashboard, and a role-only row would card every Care Manager (13.2-06's
 *   `no-owner`). `buildFollowUp` builds it; its refusals are said in plain words with the field focused.
 * - **Save** goes through the runtime's Writer (`createFollowUp`): the Action Register row, then its audit row. It is
 *   synchronous, like the Verification Log writes, so there is no undo toast: the list re-reads and a quiet "Saved"
 *   line shows. If the audit row doesn't post, the follow-up is saved and the row is owed: "Post the audit row" re-posts
 *   it (de-duped), and the item is never sent twice. Practice mode logs the payload and saves nothing.
 * - **Read only** (a closed incident, or a register role that can't add one): the list without the form.
 */

import { useId, useRef, useState } from 'react';
import { Badge, Button, Input, Select, Spinner, TextArea } from '../../ui';
import { liveChoice } from '../../../config/fieldset/live';
import { useActionRuntime } from '../../../hooks/useActionRuntime';
import { useFollowUps } from '../../../hooks/useFollowUps';
import { FOLLOW_UP_ACTION_TYPES, buildFollowUp, incidentRef } from '../../../lib/followUps';
import type { FollowUp, FollowUpBuild, FollowUpRefusal, FollowUpState } from '../../../lib/followUps';
import { TEXT_CAPS, formatDay, todayMelbourne } from '../../../lib/fieldValues';
import { isPracticeRef } from '../../../lib/recordQuery';
import { ROLE_LABEL } from '../../../lib/todayQueue';
import { CommitError } from '../../../services/actionService';
import type { FollowUpCreate } from '../../../services/actionService';
import type { Actor, AuditRowDraft, OwnerRole } from '../../../types/actionRegister';
import type { RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import styles from '../record.module.css';

const [WORKER_YES] = liveChoice('incidents', 'WorkerInjured', 'Yes');

/** The roles a follow-up can be owned under, in the order the default is picked (13.2-06). */
const OWNER_ROLE_CHOICES: readonly OwnerRole[] = ['CareManager', 'HRWHS', 'Oversight'];

type Field = 'title' | 'details' | 'ownerRole' | 'due' | 'form';

/** Each refusal, in plain words, and the field it belongs to. */
const REFUSAL_TEXT: Readonly<Record<FollowUpRefusal, { input: Field; text: string }>> = {
  'no-title': { input: 'title', text: 'Say what the follow-up is.' },
  'title-too-long': { input: 'title', text: `Keep it to ${TEXT_CAPS.text} characters, and put the rest in the details.` },
  'details-too-long': { input: 'details', text: `The details are too long: ${TEXT_CAPS.textarea.toLocaleString('en-AU')} characters at most.` },
  'bad-owner-role': { input: 'ownerRole', text: 'Choose the owner role from the list.' },
  'bad-due': { input: 'due', text: 'Choose a due date, today or later.' },
  'no-owner': {
    input: 'form',
    text: "Your account on the Quality & Safety site couldn't be read, so the follow-up can't name you as its owner. Reload the page and try again.",
  },
  'no-role': { input: 'form', text: 'Adding a follow-up needs the Care Manager, HR & WHS or Oversight role.' },
  'incident-closed': { input: 'form', text: 'This incident is closed. Oversight reopens it first to add a follow-up.' },
  'not-an-incident': { input: 'form', text: 'Follow-ups are added to incidents only.' },
};

const STATE_CHIP: Readonly<Record<FollowUpState, { label: string; variant: 'success' | 'info' | 'warning' | 'neutral' }>> = {
  done: { label: 'Done', variant: 'success' },
  scheduled: { label: 'Scheduled', variant: 'info' },
  overdue: { label: 'Overdue', variant: 'warning' },
  undated: { label: 'No due date', variant: 'neutral' },
  rejected: { label: 'Rejected', variant: 'neutral' },
};

/** The person's default owner role: CareManager, then HRWHS, then Oversight (13.2-06). */
function defaultRole(actor: Actor): OwnerRole {
  return OWNER_ROLE_CHOICES.find((r) => (actor.roles as readonly string[]).includes(r)) ?? 'CareManager';
}

/**
 * A practice record (`INC-P<n>`) has no live id: `buildFollowUp` links `INC-<n>`, so the practice log is re-pointed at
 * the practice ref. Practice only; live never opens a practice record.
 */
function forRecord(built: Extract<FollowUpBuild, { ok: true }>, row: RecordRow): FollowUpCreate {
  if (!isPracticeRef(row.ref)) return { fields: built.fields, audit: built.audit };
  const wrong = incidentRef(row.id);
  return {
    fields: { ...built.fields, SourceRef: row.ref, ParentId: row.ref },
    audit: { ...built.audit, note: built.audit.note ? built.audit.note.split(` on ${wrong},`).join(` on ${row.ref},`) : built.audit.note },
  };
}

function ownerWords(f: FollowUp, actor: Actor): string {
  const role = ROLE_LABEL[f.row.ownerRole] ?? f.row.ownerRole;
  if (actor.lookupId && f.row.ownerLookupId === actor.lookupId) return `${role}: you`;
  return f.row.owner ? `${role}: ${f.row.owner}` : role;
}

function FollowUpList({ followUps, actor }: { followUps: readonly FollowUp[]; actor: Actor }) {
  return (
    <ul className={styles.timeline} aria-label="Follow-ups">
      {followUps.map((f) => {
        const chip = STATE_CHIP[f.state];
        return (
          <li key={f.row.id} className={styles.entry}>
            <p className={styles.entryHead}>
              <span className={styles.entryHeading}>{f.row.title || 'Untitled follow-up'}</span>
              <Badge size="sm" variant={chip.variant}>
                {chip.label}
              </Badge>
              {f.row.actionType === FOLLOW_UP_ACTION_TYPES['worker-update'] && (
                <Badge size="sm" variant="neutral">
                  Worker update
                </Badge>
              )}
            </p>
            <p className={styles.entryMeta}>
              {ownerWords(f, actor)} · {f.dueOn ? `Due ${formatDay(f.dueOn)}` : 'No due date'} · {f.row.reference}
            </p>
            {f.row.description && <p className={styles.hint}>{f.row.description}</p>}
          </li>
        );
      })}
    </ul>
  );
}

export default function FollowUpsPanel({ row, actor, readOnly }: PanelProps) {
  const { writer, io } = useActionRuntime();
  const list = useFollowUps(row.ref);
  const uid = useId();
  const idOf = (name: string) => `${uid}-${name}`;
  const [today] = useState(() => todayMelbourne(new Date()));
  const workerInjured = row.fields.WorkerInjured === WORKER_YES;

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [ownerRole, setOwnerRole] = useState<OwnerRole>(() => defaultRole(actor));
  const [dueOn, setDueOn] = useState('');
  const [workerUpdate, setWorkerUpdate] = useState(false);
  const [refusals, setRefusals] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [owed, setOwed] = useState<AuditRowDraft[]>([]);
  const [posting, setPosting] = useState(false);

  const refs = {
    title: useRef<HTMLInputElement>(null),
    details: useRef<HTMLTextAreaElement>(null),
    ownerRole: useRef<HTMLSelectElement>(null),
    due: useRef<HTMLInputElement>(null),
  };

  function touch(input: Field) {
    setRefusals((r) => {
      const next = { ...r };
      delete next[input];
      delete next.form;
      return next;
    });
    setSaved(null);
    setFailure(null);
  }

  async function add() {
    if (!io || saving) return;
    setSaved(null);
    setFailure(null);
    const built = buildFollowUp(
      row,
      actor,
      { title, details, ownerRole, dueOn, kind: workerInjured && workerUpdate ? 'worker-update' : 'follow-up' },
      new Date(),
    );
    if (!built.ok) {
      const found: Partial<Record<Field, string>> = {};
      for (const code of built.refusals) found[REFUSAL_TEXT[code].input] ??= REFUSAL_TEXT[code].text;
      setRefusals(found);
      const first = (['title', 'details', 'ownerRole', 'due'] as const).find((k) => found[k]);
      if (first) refs[first].current?.focus();
      return;
    }
    setRefusals({});
    setSaving(true);
    try {
      await writer.createFollowUp(io, forRecord(built, row));
      setTitle('');
      setDetails('');
      setDueOn('');
      setWorkerUpdate(false);
      setSaved(
        writer.mode === 'practice'
          ? 'Saved in practice mode. Nothing was written to SharePoint, so it does not appear in the list.'
          : 'Saved.',
      );
      list.reload();
    } catch (err) {
      if (err instanceof CommitError && err.stage === 'audit') {
        // The follow-up exists; only its audit row is owed. Never re-send the item.
        setOwed((o) => [...o, ...err.remainingAudit]);
        setTitle('');
        setDetails('');
        setDueOn('');
        setWorkerUpdate(false);
        setSaved('Saved, but its audit row did not post yet.');
        list.reload();
      } else {
        setFailure(err instanceof Error ? err.message : 'The follow-up did not go through.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function postOwed() {
    if (!io || posting || owed.length === 0) return;
    setPosting(true);
    try {
      const still = await writer.postFollowUpAudit(io, owed);
      setOwed(still);
      if (still.length === 0) setSaved('Saved. The audit row is posted.');
    } catch {
      // Still owed: the button stays.
    } finally {
      setPosting(false);
    }
  }

  const msg = (input: Field) =>
    refusals[input] ? (
      <p id={idOf(`${input}-msg`)} className={styles.refusal}>
        {refusals[input]}
      </p>
    ) : null;
  const refused = (input: Field) => (refusals[input] ? styles.inputRefused : undefined);
  const described = (input: Field) => (refusals[input] ? idOf(`${input}-msg`) : undefined);

  return (
    <div className={styles.verbForm}>
      <p className={styles.hint}>
        What happens after lodging, each with an owner and a due date. They are Action Register items linked to this
        incident, so Oversight sees on Verify what is done and what is scheduled.
      </p>

      {list.status === 'loading' && (
        <p className={styles.hint} role="status">
          <Spinner size="sm" /> Reading the follow-ups…
        </p>
      )}
      {list.status === 'error' && (
        <div className={styles.actions}>
          <p className={styles.failure} role="alert">
            Couldn't read the follow-ups from the Action Register{list.error ? `: ${list.error}` : '.'}
          </p>
          <Button type="button" variant="secondary" size="sm" onClick={list.reload}>
            Try again
          </Button>
        </div>
      )}
      {list.status === 'ready' &&
        (list.followUps.length === 0 ? (
          <p className={styles.empty}>No follow-ups yet.</p>
        ) : (
          <FollowUpList followUps={list.followUps} actor={actor} />
        ))}

      {owed.length > 0 && (
        <div className={styles.notice} role="status">
          <p className={styles.hint}>
            {owed.length === 1 ? '1 audit row' : `${owed.length} audit rows`} still to post for a follow-up you added. The
            follow-up itself is saved.
          </p>
          <Button type="button" variant="secondary" size="sm" loading={posting} disabled={!io} onClick={() => void postOwed()}>
            Post the audit row
          </Button>
        </div>
      )}

      {!readOnly && (
        <section className={styles.composer} aria-labelledby={idOf('title-h')}>
          <h4 id={idOf('title-h')} className={styles.composerTitle}>
            Add a follow-up
          </h4>
          <fieldset className={styles.fields} disabled={saving} inert={saving || undefined}>
            <div className={styles.field}>
              <label htmlFor={idOf('title')} className={styles.label}>
                What needs to happen
              </label>
              <Input
                ref={refs.title}
                id={idOf('title')}
                value={title}
                className={refused('title')}
                aria-describedby={described('title')}
                onChange={(e) => {
                  setTitle(e.target.value);
                  touch('title');
                }}
              />
              {msg('title')}
            </div>

            <div className={styles.field}>
              <label htmlFor={idOf('details')} className={styles.label}>
                Details (optional)
              </label>
              <TextArea
                ref={refs.details}
                id={idOf('details')}
                rows={3}
                value={details}
                className={refused('details')}
                aria-describedby={described('details')}
                onChange={(e) => {
                  setDetails(e.target.value);
                  touch('details');
                }}
              />
              {msg('details')}
            </div>

            <div className={styles.field}>
              <label htmlFor={idOf('role')} className={styles.label}>
                Owner role
              </label>
              <Select
                ref={refs.ownerRole}
                id={idOf('role')}
                value={ownerRole}
                className={refused('ownerRole')}
                aria-describedby={described('ownerRole')}
                onChange={(e) => {
                  setOwnerRole(e.target.value as OwnerRole);
                  touch('ownerRole');
                }}
              >
                {OWNER_ROLE_CHOICES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
              <p className={styles.hint}>The owner is you{actor.displayName ? ` (${actor.displayName})` : ''}: a follow-up is owned by the person who adds it.</p>
              {msg('ownerRole')}
            </div>

            <div className={styles.field}>
              <label htmlFor={idOf('due')} className={styles.label}>
                Due date
              </label>
              <Input
                ref={refs.due}
                id={idOf('due')}
                type="date"
                min={today}
                value={dueOn}
                className={`${styles.dateInput} ${refused('due') ?? ''}`}
                aria-describedby={described('due')}
                onChange={(e) => {
                  setDueOn(e.target.value);
                  touch('due');
                }}
              />
              {msg('due')}
            </div>

            {workerInjured && (
              <div className={styles.field}>
                <label className={styles.checkTick}>
                  <input
                    type="checkbox"
                    checked={workerUpdate}
                    onChange={(e) => {
                      setWorkerUpdate(e.target.checked);
                      touch('form');
                    }}
                  />
                  <span>This is the injured worker's update</span>
                </label>
                <p className={styles.hint}>
                  How the worker hears how it ended: usually a ShiftCare note, a shift change or a conversation. Oversight
                  checks one is done or scheduled before closing.
                </p>
              </div>
            )}
          </fieldset>

          {refusals.form && (
            <p className={styles.refusal} role="alert">
              {refusals.form}
            </p>
          )}
          {failure && (
            <p className={styles.failure} role="alert">
              Couldn't add that: {failure}
            </p>
          )}
          {saved && (
            <p className={styles.saved} role="status">
              <span className={styles.tick} aria-hidden="true">
                ✓
              </span>{' '}
              {saved}
            </p>
          )}

          <div className={styles.actions}>
            {!io && <span className={styles.hint}>Connecting to SharePoint…</span>}
            <Button type="button" loading={saving} disabled={!io} onClick={() => void add()}>
              Add the follow-up
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
